import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, downloadFile, formatApiErrorDetail } from "../lib/api";
import DropZone from "../components/DropZone";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function PdfExtract() {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [pages, setPages] = useState([]); // {page, data_url, selected}
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  async function loadThumbs(f) {
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/pdf/thumbnails", fd);
      setPages(data.pages.map((p) => ({ ...p, selected: false })));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setLoading(false);
  }

  async function onSubmit() {
    const selected = pages.filter((p) => p.selected).map((p) => p.page);
    if (selected.length === 0) {
      toast.error("Select at least one page");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("pages", selected.join(","));
      const { data } = await api.post("/pdf/extract", fd);
      setResult(data);
      toast.success(t("success"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  function toggle(p) {
    setPages((prev) => prev.map((x) => (x.page === p ? { ...x, selected: !x.selected } : x)));
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12" data-testid="pdf-extract-page">
      <Link to="/tools/pdf" className="eyebrow text-zinc-500 hover:text-black">← {t("cta_back")}</Link>
      <h1 className="hero-h2 text-3xl sm:text-5xl mt-2">{t("tool_extract")}</h1>
      <p className="text-zinc-500 mt-2">{t("tool_extract_desc")}</p>

      <div className="mt-8">
        {!file ? (
          <DropZone accept="application/pdf" onFiles={(fs) => { setFile(fs[0]); loadThumbs(fs[0]); }} testId="extract-dropzone" />
        ) : (
          <>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
              <div>
                <span className="eyebrow">{file.name}</span>
                <div className="text-zinc-500 text-sm mt-1">{t("pdf_select_pages")}</div>
              </div>
              <div className="flex gap-3">
                <Button variant="ghost" className="rounded-sm" onClick={() => { setFile(null); setPages([]); setResult(null); }} data-testid="extract-clear">
                  {t("cta_clear")}
                </Button>
                <Button onClick={onSubmit} className="rounded-sm bg-black text-white hover:bg-zinc-800" data-testid="extract-submit">
                  {t("cta_apply")} →
                </Button>
              </div>
            </div>

            {loading && <div className="text-zinc-500 text-sm">{t("pdf_pages_loading")}</div>}

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="extract-pages-grid">
              {pages.map((p) => (
                <button
                  key={p.page}
                  className={`page-tile ${p.selected ? "selected" : ""} p-2 rounded-md flex flex-col items-center transition-all`}
                  onClick={() => toggle(p.page)}
                  data-testid={`page-${p.page}`}
                >
                  <img src={p.data_url} alt={`Page ${p.page}`} className="w-full h-auto" />
                  <span className="mt-2 eyebrow">Page {p.page}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {result && (
          <div className="mt-6 border border-black rounded-md p-6 bg-zinc-50 fade-up" data-testid="extract-result">
            <span className="eyebrow">{t("success")}</span>
            <div className="hero-h2 text-xl mt-1">{result.original_filename}</div>
            <div className="mt-4">
              <Button onClick={() => downloadFile(result.id, result.original_filename)}
                className="rounded-sm bg-black text-white hover:bg-zinc-800 h-10" data-testid="extract-download">
                ↓ {t("cta_download")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
